import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

type Step = "account" | "profile" | "niche" | "done";

const niches = [
  { id: "mindset", label: "Mindset Coaching", icon: "ri-mental-health-line" },
  { id: "life", label: "Life Coaching", icon: "ri-heart-pulse-line" },
  { id: "career", label: "Career Coaching", icon: "ri-briefcase-4-line" },
  { id: "corporate", label: "Corporate Success", icon: "ri-building-4-line" },
  { id: "wellness", label: "Wellness & Resilience", icon: "ri-leaf-line" },
  { id: "relationships", label: "Relationships", icon: "ri-team-line" },
];

const roles = [
  { id: "learner", label: "I'm a Learner", desc: "I want to grow and transform through coaching programs", icon: "ri-book-open-line" },
  { id: "coach", label: "I'm a Coach", desc: "I want to share my expertise and earn from my programs", icon: "ri-award-line" },
];

export default function RegisterPage() {
  const [step, setStep] = useState<Step>("account");
  const [role, setRole] = useState<string>("");
  const [selectedNiches, setSelectedNiches] = useState<string[]>([]);
  const [form, setForm] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: "",
    displayName: "",
    bio: "",
    location: "",
    website: "",
  });

  const steps: Step[] = ["account", "profile", "niche", "done"];
  const stepIdx = steps.indexOf(step);

  const toggleNiche = (id: string) => {
    setSelectedNiches((prev) =>
      prev.includes(id) ? prev.filter((n) => n !== id) : [...prev, id]
    );
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  return (
    <div className="min-h-screen bg-[#F7F6F3]">
      <Navbar />
      <main className="pt-24 pb-16">
        <div className="max-w-2xl mx-auto px-6">
          {/* Header */}
          <div className="text-center mb-10">
            <img
              src="https://static.readdy.ai/image/64d067dc58cbae89e71ec40faad2636e/deaaf3708287f522a170a22cd3f29889.jpeg"
              alt="Mind Odyssey"
              className="w-14 h-14 rounded-full object-cover mx-auto mb-4"
            />
            <h1
              className="text-3xl font-bold text-gray-900 mb-2"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {step === "done" ? "Welcome to Mind Odyssey!" : "Create Your Account"}
            </h1>
            <p className="text-gray-500 text-sm">
              {step === "done"
                ? "Your journey to limitless growth starts now."
                : "Join 12,000+ members transforming their lives"}
            </p>
          </div>

          {/* Progress */}
          {step !== "done" && (
            <div className="flex items-center gap-2 mb-8">
              {["Account", "Profile", "Interests"].map((label, i) => (
                <div key={label} className="flex items-center gap-2 flex-1">
                  <div className="flex items-center gap-2">
                    <div
                      className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-semibold transition-all ${
                        i < stepIdx
                          ? "bg-teal-500 text-white"
                          : i === stepIdx
                          ? "bg-teal-600 text-white"
                          : "bg-gray-200 text-gray-400"
                      }`}
                    >
                      {i < stepIdx ? <i className="ri-check-line" /> : i + 1}
                    </div>
                    <span className={`text-xs font-medium hidden sm:block ${i <= stepIdx ? "text-teal-600" : "text-gray-400"}`}>
                      {label}
                    </span>
                  </div>
                  {i < 2 && <div className={`flex-1 h-0.5 ${i < stepIdx ? "bg-teal-400" : "bg-gray-200"}`} />}
                </div>
              ))}
            </div>
          )}

          {/* Card */}
          <div className="bg-white rounded-2xl border border-gray-100 p-8">
            {/* Step 1: Account */}
            {step === "account" && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Account Details</h2>

                {/* Role Selection */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-6">
                  {roles.map((r) => (
                    <button
                      key={r.id}
                      onClick={() => setRole(r.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                        role === r.id ? "border-teal-500 bg-teal-50" : "border-gray-200 hover:border-teal-200"
                      }`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center mb-2">
                        <i className={`${r.icon} text-xl ${role === r.id ? "text-teal-600" : "text-gray-400"}`} />
                      </div>
                      <p className="font-semibold text-gray-900 text-sm">{r.label}</p>
                      <p className="text-gray-400 text-xs mt-1">{r.desc}</p>
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">First Name</label>
                    <input
                      name="firstName"
                      value={form.firstName}
                      onChange={handleChange}
                      placeholder="Alex"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1.5">Last Name</label>
                    <input
                      name="lastName"
                      value={form.lastName}
                      onChange={handleChange}
                      placeholder="Rivera"
                      className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Email Address</label>
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={handleChange}
                    placeholder="alex@email.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Password</label>
                  <input
                    name="password"
                    type="password"
                    value={form.password}
                    onChange={handleChange}
                    placeholder="Min. 8 characters"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Confirm Password</label>
                  <input
                    name="confirmPassword"
                    type="password"
                    value={form.confirmPassword}
                    onChange={handleChange}
                    placeholder="Repeat password"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <button
                  onClick={() => setStep("profile")}
                  className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                >
                  Continue
                </button>

                <p className="text-center text-xs text-gray-400">
                  Already have an account?{" "}
                  <Link to="/dashboard" className="text-teal-600 font-medium cursor-pointer">
                    Sign In
                  </Link>
                </p>
              </div>
            )}

            {/* Step 2: Profile */}
            {step === "profile" && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-6">Build Your Profile</h2>

                {/* Avatar Upload */}
                <div className="flex items-center gap-5 mb-4">
                  <div className="w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center border-2 border-dashed border-teal-300 cursor-pointer hover:bg-teal-50 transition-colors">
                    <i className="ri-camera-line text-teal-500 text-2xl" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-900">Profile Photo</p>
                    <p className="text-xs text-gray-400 mt-1">Upload a photo to personalize your profile</p>
                    <button className="mt-2 text-xs text-teal-600 font-medium cursor-pointer whitespace-nowrap">
                      Upload Photo
                    </button>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Display Name</label>
                  <input
                    name="displayName"
                    value={form.displayName}
                    onChange={handleChange}
                    placeholder="How you'll appear to others"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Bio</label>
                  <textarea
                    name="bio"
                    value={form.bio}
                    onChange={handleChange}
                    placeholder="Tell the community about yourself, your journey, and what you're passionate about..."
                    rows={4}
                    maxLength={500}
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors resize-none"
                  />
                  <p className="text-xs text-gray-400 text-right mt-1">{form.bio.length}/500</p>
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Location</label>
                  <input
                    name="location"
                    value={form.location}
                    onChange={handleChange}
                    placeholder="City, Country"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1.5">Website / LinkedIn (optional)</label>
                  <input
                    name="website"
                    value={form.website}
                    onChange={handleChange}
                    placeholder="https://yourwebsite.com"
                    className="w-full border border-gray-200 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-teal-400 transition-colors"
                  />
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setStep("account")}
                    className="flex-1 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep("niche")}
                    className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Continue
                  </button>
                </div>
              </div>
            )}

            {/* Step 3: Niche */}
            {step === "niche" && (
              <div className="space-y-5">
                <h2 className="text-lg font-semibold text-gray-900 mb-2">Your Coaching Interests</h2>
                <p className="text-gray-500 text-sm mb-6">
                  Select the areas you're most interested in. This helps us personalize your experience.
                </p>

                <div className="grid grid-cols-2 gap-3">
                  {niches.map((n) => (
                    <button
                      key={n.id}
                      onClick={() => toggleNiche(n.id)}
                      className={`p-4 rounded-xl border-2 text-left transition-all cursor-pointer ${
                        selectedNiches.includes(n.id)
                          ? "border-teal-500 bg-teal-50"
                          : "border-gray-200 hover:border-teal-200"
                      }`}
                    >
                      <div className="w-8 h-8 flex items-center justify-center mb-2">
                        <i className={`${n.icon} text-xl ${selectedNiches.includes(n.id) ? "text-teal-600" : "text-gray-400"}`} />
                      </div>
                      <p className="font-medium text-gray-900 text-sm">{n.label}</p>
                    </button>
                  ))}
                </div>

                <div className="flex gap-3 mt-6">
                  <button
                    onClick={() => setStep("profile")}
                    className="flex-1 py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Back
                  </button>
                  <button
                    onClick={() => setStep("done")}
                    className="flex-1 py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap"
                  >
                    Complete Profile
                  </button>
                </div>
              </div>
            )}

            {/* Step 4: Done */}
            {step === "done" && (
              <div className="text-center py-8">
                <div className="w-20 h-20 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-6">
                  <i className="ri-check-double-line text-teal-600 text-4xl" />
                </div>
                <h2
                  className="text-2xl font-bold text-gray-900 mb-3"
                  style={{ fontFamily: "'Playfair Display', serif" }}
                >
                  You're all set!
                </h2>
                <p className="text-gray-500 text-sm mb-8 max-w-xs mx-auto">
                  Your Mind Odyssey profile is ready. Start exploring programs, connect with coaches, and begin your transformation.
                </p>
                <div className="flex flex-col gap-3">
                  <Link
                    to="/dashboard"
                    className="w-full py-3 bg-teal-600 hover:bg-teal-700 text-white font-semibold rounded-xl transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    Go to Dashboard
                  </Link>
                  <Link
                    to="/programs"
                    className="w-full py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    Browse Programs
                  </Link>
                  <Link
                    to="/community"
                    className="w-full py-3 border border-gray-200 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-center"
                  >
                    Explore Community
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
